/**
 * Merge a restored autosave draft on top of already-initialized form data
 * (which may include viewer prefill) without letting *empty* draft values
 * destroy non-empty values that are already present.
 *
 * Why: autosave persists the entire form — including fields the user never
 * touched — as empty strings. A naive `{ ...current, ...draftData }` spread lets
 * those empties clobber prefilled identity/attendee values, so a signed-in
 * attendee whose draft happens to have blank name/dietary/tshirt fields sees an
 * empty form even though prefill populated them. The rule here is simple and
 * field-agnostic: a blank in the draft never overwrites a value the field
 * already has; a non-empty draft value (the user's real input) always wins.
 *
 * Note: if a user deliberately cleared a prefilled field and the draft saved it
 * blank, reopening re-applies the prefill. That's an acceptable trade for an
 * auto-fill feature — the user can clear it again — and is far better than every
 * attendee with a stale empty draft seeing a blank form.
 */

/** Phone fields store an object value; treat a blank phone string as empty. */
function isEmptyFormValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object" && "phone" in (value as Record<string, unknown>)) {
    const phone = (value as { phone?: unknown }).phone;
    return typeof phone !== "string" || phone.trim() === "";
  }
  return false;
}

/**
 * Overlay `draftData` onto `current`, keeping the current value whenever the
 * draft value is empty but the current value is not.
 *
 * @param current - The initialized/prefilled form data to merge onto.
 * @param draftData - The restored draft values keyed by field label.
 * @returns A new form-data map with the draft applied, prefill preserved for
 *   fields the draft left blank.
 */
export function mergeDraftOverFormData(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form data values are heterogeneous (strings, arrays, phone objects)
  current: Record<string, any>,
  draftData: Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  const next = { ...current };

  for (const [label, draftValue] of Object.entries(draftData)) {
    if (isEmptyFormValue(draftValue) && !isEmptyFormValue(next[label])) continue;
    next[label] = draftValue;
  }

  return next;
}
